import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PaymentDetailDto } from "@/services/admin-payment.service";
import { format } from "date-fns";
import {
  Check,
  X,
  Copy,
  ExternalLink,
  Download,
  StickyNote,
} from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { PaymentStatusBadge } from "./PaymentStatusBadge";
import { PaymentProofLightbox } from "./PaymentProofLightbox";
import { AuthedImage } from "./AuthedImage";
import { Separator } from "@/components/ui/separator";
import { apiClient } from "@/lib/api";
import {
  isRenderableImage,
  proofDownloadPath,
  proofFileNameOf,
} from "@/lib/paymentProof";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";
import { PaymentNoteDialog } from "./PaymentNoteDialog";

interface PaymentDetailCardProps {
  payment: PaymentDetailDto;
  onVerify?: () => void;
  onReject?: () => void;
  isVerifying?: boolean;
  isRejecting?: boolean;
  /** Set false when the proof is rendered elsewhere (PaymentProofPanel) on the same screen. */
  showProof?: boolean;
}

export function PaymentDetailCard({
  payment,
  onVerify,
  onReject,
  isVerifying,
  isRejecting,
  showProof = true,
}: PaymentDetailCardProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);

  // Writing the note is SuperAdmin-only, mirroring the [AdminRole] gate on the endpoint. Reading
  // it is not: the note is there so whoever picks the payment up next knows what is going on.
  const { isSuperAdmin } = usePermissions();
  const canEditNote = isSuperAdmin();

  // What the decision block below is describing.
  //
  // "Failed" is unambiguous here: Payment.Reject is the ONLY path that sets that status, and it is
  // also the only one that stamps VerifiedBy — so a Failed payment carrying a decider was refused
  // by that person, never approved by them.
  //
  // Refunded deliberately keeps the verify wording: the refund does not touch VerifiedBy/VerifiedAt,
  // which still name whoever approved the payment in the first place (Payment.Refund only runs on a
  // Completed payment). It DOES overwrite VerificationNotes with "Refunded by admin. Reason: …",
  // so the note section below is titled for what it now holds rather than for the approval.
  const decision =
    payment.status === "Failed"
      ? { actorLabel: "Rejected By:", timeLabel: "Rejected At:", noteTitle: "Rejection Reason" }
      : payment.status === "Refunded"
        ? { actorLabel: "Verified By:", timeLabel: "Verified At:", noteTitle: "Refund Note" }
        : { actorLabel: "Verified By:", timeLabel: "Verified At:", noteTitle: "Note" };

  // Proof written straight onto the payment, before attachments existed. Aliased to a const so
  // the callbacks below keep the narrowing the render guard gives them.
  const legacyProofUrl = payment.paymentProofUrl;
  const legacyProofIsImage =
    !!legacyProofUrl && isRenderableImage({ fileName: legacyProofUrl });

  // Indexed by download URL, not by position: the gallery below renders every attachment
  // while only the images reach the lightbox, so an attachment's index in `payment.attachments`
  // is not its index here as soon as one non-image sits in front of it.
  const lightboxImages = [
    ...(payment.attachments ?? [])
      .filter((a) => a.contentType.startsWith("image/"))
      .map((a) => ({
        src: proofDownloadPath(a.downloadUrl),
        alt: a.fileName,
        fileName: a.fileName,
        contentType: a.contentType,
      })),
    ...(legacyProofUrl && legacyProofIsImage
      ? [
          {
            src: proofDownloadPath(legacyProofUrl),
            alt: "Payment proof",
            fileName: proofFileNameOf(legacyProofUrl),
          },
        ]
      : []),
  ];

  const openLightbox = (src: string) => {
    const index = lightboxImages.findIndex((image) => image.src === src);
    if (index < 0) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <Card className="rounded-xl border border-border/80 shadow-xs">
      <CardHeader className="pb-4 border-b border-border/40">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-bold text-foreground">
            Payment Details
          </CardTitle>
          <PaymentStatusBadge status={payment.status} />
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Key Information - Hero Amount Banner */}
        <div className="rounded-xl border border-primary/15 bg-gradient-to-r from-primary/[0.06] to-primary/[0.01] p-4 space-y-3 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Amount
            </span>
            <span className="text-2xl font-extrabold text-foreground tracking-tight">
              {formatCurrency(payment.amount, payment.currency)}
            </span>
          </div>
          <Separator className="bg-primary/10" />
          <div className="flex items-center justify-between gap-2">
            {/* This is the PAYMENT's own reference (WIC-PAY-…), not the order's — the order it
                settles has its own WIC-ORD- number further down. Labelling both "Order Number"
                sent operators quoting the wrong one back to the customer. */}
            <span className="text-xs font-medium text-muted-foreground">Payment Number:</span>
            <div className="flex items-center gap-1.5 bg-background/80 px-2.5 py-1 rounded-md border border-border/60">
              <span className="font-semibold text-xs text-primary">
                {payment.paymentOrderNumber}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 hover:bg-muted text-muted-foreground"
                onClick={() => copyToClipboard(payment.paymentOrderNumber)}
                title="Copy payment number"
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
          {/* The gateway's own code for this bill. T-PayGate's records and its callbacks quote
              it, so it is what an operator matches against the gateway. */}
          {payment.tPayGateBillCode && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-muted-foreground">
                T-PayGate Bill Code:
              </span>
              <div className="flex items-center gap-1.5 bg-background/80 px-2.5 py-1 rounded-md border border-border/60">
                <span className="font-mono font-semibold text-xs text-foreground">
                  {payment.tPayGateBillCode}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 hover:bg-muted text-muted-foreground"
                  onClick={() =>
                    copyToClipboard(payment.tPayGateBillCode || "")
                  }
                  title="Copy bill code"
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Primary Details Grid */}
        <div className="rounded-xl border border-border/60 p-3.5 bg-card space-y-2.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Tenant:</span>
            <span className="font-semibold text-foreground">{payment.tenantName}</span>
          </div>
          <Separator className="bg-border/30" />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Method:</span>
            <Badge variant="outline" className="text-[11px] font-medium px-2 py-0.5 rounded-md">
              {payment.paymentMethod}
            </Badge>
          </div>
          <Separator className="bg-border/30" />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Created:</span>
            <span className="font-medium text-foreground">
              {format(new Date(payment.createdAt), "MMM d, yyyy, h:mm a")}
            </span>
          </div>
        </div>

        {/* Related Information */}
        {(payment.order || payment.invoice) && (
          <div className="rounded-xl border border-border/60 p-3.5 bg-card space-y-2.5 text-xs">
            {payment.order && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Order:</span>
                <div className="flex items-center gap-1.5">
                  <Link
                    to={`/admin/orders/${payment.order.orderId}`}
                    className="font-semibold text-xs text-primary hover:underline"
                  >
                    {payment.order.orderNumber}
                  </Link>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {payment.order.status}
                  </Badge>
                </div>
              </div>
            )}
            {payment.order && payment.invoice && <Separator className="bg-border/30" />}
            {payment.invoice && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Invoice:</span>
                <div className="flex items-center gap-1.5">
                  <Link
                    to={`/admin/invoices/${payment.invoice.invoiceId}`}
                    className="font-semibold text-xs text-primary hover:underline"
                  >
                    {payment.invoice.invoiceNumber}
                  </Link>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {payment.invoice.status}
                  </Badge>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Transaction Information */}
        {payment.tPaymentTransactionId && (
          <div className="rounded-xl border border-border/60 p-3.5 bg-card space-y-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="text-muted-foreground">Transaction ID:</span>
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-xs text-foreground bg-muted/60 px-2 py-0.5 rounded">
                  {payment.tPaymentTransactionId}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 text-muted-foreground"
                  onClick={() =>
                    copyToClipboard(payment.tPaymentTransactionId || "")
                  }
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>
            {payment.tPaymentRedirectUrl && (
              <div className="flex items-center justify-end pt-1">
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs text-primary font-medium"
                  onClick={() =>
                    window.open(payment.tPaymentRedirectUrl, "_blank")
                  }
                >
                  View Transaction <ExternalLink className="ml-1 h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Who decided this payment, and when.
            The labels follow the STATUS, because the three columns behind them are shared: both
            Payment.Verify and Payment.Reject write VerifiedBy / VerifiedAt / VerificationNotes.
            Labelling them "Verified By" unconditionally put the approving wording on a refusal —
            a rejected payment rendered "Verified By: <operator>" directly under a red Failed
            badge, which reads as an approval by the very person who turned it down. */}
        {payment.verifiedBy && (
          <div className="rounded-xl border border-border/60 p-3.5 bg-card space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{decision.actorLabel}</span>
              <span className="font-semibold text-foreground">
                {/* Never `?? payment.verifiedBy`: that fell back to the raw GUID, which an
                    operator cannot read, and it fired on the 13 of 242 decided payments on dev
                    whose account has since been deleted. The e-mail leads because it is what
                    operators recognise each other by and is unique where a name is not. */}
                {payment.verifiedByEmail ?? payment.verifiedByName ?? "N/A"}
              </span>
            </div>
            {payment.verifiedAt && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{decision.timeLabel}</span>
                <span className="font-medium text-foreground">
                  {format(new Date(payment.verifiedAt), "MMM d, yyyy, h:mm a")}
                </span>
              </div>
            )}
          </div>
        )}

        {/* The payment's note.
            One field, two jobs: whatever the last decision left behind (verification notes, a
            rejection reason, the refund sentence) and an operator's own note, which a SuperAdmin
            may write in any status — Completed included. It has no column of its own (decided
            2026-09-21), which is why the caption states the two consequences: the customer reads
            this text, and the next decision replaces it.
            Rendered whenever there is a note, and also when the viewer may write one — otherwise a
            SuperAdmin looking at a payment with no note would have nowhere to add it. */}
        {(payment.verificationNotes || canEditNote) && (
          <div className="rounded-xl border border-border/60 p-3.5 bg-card space-y-2 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-foreground">
                {decision.noteTitle}
              </span>
              {canEditNote && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 gap-1 px-2 text-xs text-primary"
                  onClick={() => setNoteDialogOpen(true)}
                >
                  <StickyNote className="h-3 w-3" />
                  {payment.verificationNotes ? "Edit" : "Add"}
                </Button>
              )}
            </div>
            {payment.verificationNotes ? (
              // whitespace-pre-wrap: an operator's note carries its own line breaks, and a
              // reconciliation note run together into one paragraph is unreadable.
              <p className="whitespace-pre-wrap break-words text-foreground">
                {payment.verificationNotes}
              </p>
            ) : (
              <p className="text-muted-foreground">No note yet.</p>
            )}
            {canEditNote && (
              <p className="border-t border-border/30 pt-1 text-[11px] text-muted-foreground">
                The customer sees this text on their payment screen, and
                verifying, rejecting, cancelling or refunding the payment
                replaces it.
              </p>
            )}
          </div>
        )}

        {/* Payment Proof Gallery — hidden when the page renders the proof panel instead */}
        {showProof &&
          ((payment.attachments && payment.attachments.length > 0) ||
            payment.paymentProofUrl) && (
          <div className="rounded-xl border border-border/60 p-3.5 bg-card space-y-2.5">
            <p className="text-xs font-semibold text-foreground flex items-center justify-between">
              <span>Payment Proof</span>
              {payment.attachments && payment.attachments.length > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {payment.attachments.length} file{payment.attachments.length > 1 ? "s" : ""}
                </Badge>
              )}
            </p>
            <div className="space-y-2">
              {payment.attachments && payment.attachments.length > 0
                ? payment.attachments.map((attachment) => (
                    <div key={attachment.refId} className="relative group">
                      {attachment.contentType.startsWith("image/") ? (
                        <button
                          type="button"
                          className="w-full text-left cursor-pointer overflow-hidden rounded-lg border bg-muted/30 transition-all hover:border-primary group-hover:shadow-md"
                          onClick={() =>
                            openLightbox(proofDownloadPath(attachment.downloadUrl))
                          }
                        >
                          <AuthedImage
                            src={proofDownloadPath(attachment.downloadUrl)}
                            alt={attachment.fileName}
                            className="w-full max-h-56 object-contain rounded-lg transition-transform group-hover:scale-[1.01]"
                          />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="flex items-center gap-2 rounded-lg border p-3 hover:bg-muted/50 transition-colors w-full text-left cursor-pointer"
                          onClick={async () => {
                            try {
                              const res = await apiClient.get(
                                proofDownloadPath(attachment.downloadUrl),
                                { responseType: "blob" },
                              );
                              const url = URL.createObjectURL(res.data);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = attachment.fileName;
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch {
                              toast.error("Failed to download file");
                            }
                          }}
                        >
                          <Download className="h-4 w-4 shrink-0 text-primary" />
                          <span className="text-xs font-medium truncate">
                            {attachment.fileName}
                          </span>
                          <span className="text-[10px] text-muted-foreground ml-auto">
                            {(attachment.fileSize / 1024).toFixed(1)} KB
                          </span>
                        </button>
                      )}
                    </div>
                  ))
                : legacyProofUrl && (
                    <div className="relative group">
                      {!legacyProofIsImage ? (
                        <button
                          type="button"
                          className="flex items-center gap-2 rounded-lg border p-3 hover:bg-muted/50 transition-colors w-full text-left cursor-pointer"
                          onClick={async () => {
                            try {
                              const res = await apiClient.get(
                                proofDownloadPath(legacyProofUrl),
                                { responseType: "blob" },
                              );
                              const url = URL.createObjectURL(res.data);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = proofFileNameOf(legacyProofUrl);
                              a.click();
                              URL.revokeObjectURL(url);
                            } catch {
                              toast.error("Failed to download file");
                            }
                          }}
                        >
                          <Download className="h-4 w-4 shrink-0 text-primary" />
                          <span className="text-xs font-medium">
                            Download Payment Proof
                          </span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="w-full text-left cursor-pointer overflow-hidden rounded-lg border bg-muted/30 transition-all hover:border-primary group-hover:shadow-md"
                          onClick={() =>
                            openLightbox(proofDownloadPath(legacyProofUrl))
                          }
                        >
                          <AuthedImage
                            src={proofDownloadPath(legacyProofUrl)}
                            alt="Payment proof"
                            className="w-full max-h-56 object-contain rounded-lg transition-transform group-hover:scale-[1.01]"
                          />
                        </button>
                      )}
                    </div>
                  )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {(payment.status === "Pending" ||
          payment.status === "AwaitingVerification") &&
          onVerify &&
          onReject && (
            <div className="flex gap-2.5 pt-2">
              <Button
                onClick={onVerify}
                disabled={isVerifying || isRejecting}
                className="flex-1 bg-ok text-white hover:bg-ok/90 font-semibold shadow-xs h-10"
                size="sm"
              >
                <Check className="mr-1.5 h-4 w-4" />
                Verify Payment
              </Button>
              <Button
                onClick={onReject}
                disabled={isVerifying || isRejecting}
                variant="outline"
                className="flex-1 border-destructive/40 text-destructive hover:bg-destructive/10 font-semibold h-10"
                size="sm"
              >
                <X className="mr-1.5 h-4 w-4" />
                Reject Payment
              </Button>
            </div>
          )}
      </CardContent>

      <PaymentProofLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
      />

      {/* Mounted only while open: it owns a mutation, and nothing on this card needs it until
          the operator asks to write. */}
      {canEditNote && noteDialogOpen && (
        <PaymentNoteDialog
          paymentId={payment.paymentId}
          paymentOrderNumber={payment.paymentOrderNumber}
          note={payment.verificationNotes}
          open={noteDialogOpen}
          onOpenChange={setNoteDialogOpen}
        />
      )}
    </Card>
  );
}
